import { redirectToUrl } from "@/src/utils/safeNavigation";

export const BACKGROUND_REQUEST_HEADER = "X-Easy-Background-Request";
export const LOGIN_REQUIRED_HEADER = "X-Easy-Login-Required";
export const LOGIN_REDIRECT_GUARD_WINDOW_MS = 60_000;
const LOGIN_REDIRECT_GUARD_KEY = "easy.loginRedirectedAt";

export function redirectToLoginIfSessionExpired(status: number): boolean {
  return status === 401 && !!window.EASY.currentUser?.logged && redirectToLogin();
}

export function redirectToLoginIfSessionLost(response: Response): boolean {
  const sessionLost = isLoginRequired(response) || isRedirectToLogin(response);
  if (!sessionLost || !isLoggedIn()) return false;

  // Even when the guard holds the redirect back, the response is a login page or an empty 401, not content to render
  redirectToLogin();
  return true;
}

/** Resolves to null when the session is lost; the redirect to login is then already handled. */
export async function fetchWithSessionCheck(url: string, init: RequestInit = {}): Promise<Response | null> {
  const headers = new Headers(init.headers);
  headers.set(BACKGROUND_REQUEST_HEADER, "1");
  const response = await fetch(url, { ...init, headers });
  return redirectToLoginIfSessionLost(response) ? null : response;
}

function redirectToLogin(): boolean {
  // Login sends a still-logged-in user straight back, so a second redirect this soon (or unguarded) would loop
  if (!window.__easyRedirectingToLogin && !claimLoginRedirect()) return false;

  try {
    window.EasyEditor?.flushPendingAutosave?.();
  } catch {
    // Storage failure must not block the login redirect.
  }

  window.__easyRedirectingToLogin = true;
  window.onbeforeunload = null;
  const backUrl = encodeURIComponent(window.location.href);
  redirectToUrl(`${loginPath()}?back_url=${backUrl}`);
  return true;
}

// Portal pages run with an anonymous core user, so they flag their own login
function isLoggedIn(): boolean {
  return !!(window.EASY.currentUser?.logged || window.EASY.portalUserLogged);
}

function claimLoginRedirect(): boolean {
  try {
    const elapsed = Date.now() - Number(sessionStorage.getItem(LOGIN_REDIRECT_GUARD_KEY));
    if (elapsed >= 0 && elapsed < LOGIN_REDIRECT_GUARD_WINDOW_MS) return false;

    sessionStorage.setItem(LOGIN_REDIRECT_GUARD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

function isLoginRequired(response: Response): boolean {
  return response.status === 401 && response.headers.has(LOGIN_REQUIRED_HEADER);
}

function isRedirectToLogin(response: Response): boolean {
  return response.redirected && isLoginUrl(response.url);
}

function isLoginUrl(url: string): boolean {
  const { origin } = window.location;
  return new URL(url, origin).pathname === new URL(loginPath(), origin).pathname;
}

function loginPath(): string {
  return `${window.urlPrefix || ""}/login`;
}
